import '@/theme/partials/_status.scss';

import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import {
  InputChangeEventDetail,
  IonButton,
  IonButtons,
  IonContent,
  IonIcon,
  IonInput,
  IonLabel,
  IonModal,
  IonPage,
  IonText,
  IonToolbar,
  isPlatform,
  useIonViewDidEnter,
} from '@ionic/react';
import { DniReader } from '@pepper/capacitor-dni-reader';
import { AnyAction } from '@reduxjs/toolkit';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { idniecap } from 'idniecap';
import {
  addCircleOutline,
  chevronDownOutline,
  closeOutline,
  informationCircleOutline,
  searchOutline,
} from 'ionicons/icons';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import CalendarIcon from '@/assets/icons/calendar.svg';
import CaretDownRedIcon from '@/assets/icons/caret-down-red.svg';
import DotsMatrixIcon from '@/assets/icons/dots-matrix.svg';
import PlusIcon from '@/assets/icons/plus.svg';
import Banner from '@/components/common/Banner';
import { BannerType } from '@/components/common/Banner/Banner';
import Notifications from '@/components/common/NotificationModal/NotificationModal';
import StatusAlert, {
  StatusAlertActionId,
  StatusAlertId,
  useResolvedStatusAlert,
} from '@/components/common/StatusAlert';
import DasboardTableDesktopHead from '@/components/operations/DashboardTable/_layouts/DashboardTableDesktop/DasboardTableDesktopHead/DasboardTableDesktopHead';
import DashboardTable from '@/components/operations/DashboardTable/DashboardTable';
import DateRangeErrorMessage from '@/components/operations/DateRangeErrorMessage/DateRangeErrorMessage';
import EDniModal from '@/components/operations/EDniModal/EDniModal';
import FilterCalendar from '@/components/operations/FilterCalendar/FilterCalendar';
import OperationManged from '@/components/operations/ManagedModal/OperationManaged';
import OperationsFiltersModal from '@/components/operations/OperationsFiltersModal';
import { OperationsFiltersModalProps } from '@/components/operations/OperationsFiltersModal/OperationsFiltersModal';
import SearchDatePicker, {
  SearchDatePickerRef,
} from '@/components/operations/SearchDatePicker/SearchDatePicker';
import { ROUTES_PATH } from '@/constants';
import ENVIRONMENT from '@/environments/environment';
import useMediaQuery, { Breakpoint } from '@/hooks/useMediaQuery';
import useModal from '@/hooks/useModal';
import {
  ClientTaxonomyEvents,
  ClientTaxonomyProps,
} from '@/interfaces/AnalyticsInterface/AnalyticsInterface';
import { DashboardColumns } from '@/interfaces/DashboardInterface/DashboardInterface';
import {
  FilterDashboard,
  InitialStatus,
  Status,
} from '@/interfaces/OperationsDashboardInterface/OperationsDashboardInterface';
import { MonthSummary } from '@/interfaces/ReportingInterface/ReportingInterface';
import { VersionControl } from '@/interfaces/VersionControlInterface/VersionControlInterface';
import { postLeads } from '@/redux/auth/authAPI';
import {
  checkmanagedOffers,
  checkpreferredOfferAndNoPreferredOffer,
  checkValueShowSelect,
  existingCommercialOffers,
  existingEcoOffers,
} from '@/redux/commercialOffers/commercialOffers';
import { saveVersionControl } from '@/redux/configOptions/config';
import { saveEcommercePlatformData } from '@/redux/ecommerceWidget/ecommerceWidget';
import { getPlatform } from '@/redux/ecommerceWidget/ecommerceWidgetAPI';
import { addError } from '@/redux/error/error';
import {
  saveCartId,
  saveEndDate,
  saveFilters,
  saveNameOrDni,
  saveProposalRef,
  saveStartDate,
} from '@/redux/filters/filters';
import { existingCartId } from '@/redux/invoiceManaged/cartIdManaged';
import { saveMerchantData } from '@/redux/merchant/merchant';
import { updateOperationStatus } from '@/redux/operation/operations';
import { getDashboards, getMerchants } from '@/redux/operation/operationsAPI';
import {
  CommercialOffer,
  CommercialOfferSimulations,
  Dashboard,
  Dashboards,
  FlowCommercialOffer,
  FlowType,
  Merchant,
  OrderSortType,
  OrderType,
  SortType,
} from '@/redux/operation/operationsInterface';
import {
  clearIsPreferredOffer,
  saveIsPreferredOffer,
} from '@/redux/orderManaged/orderManaged';
import { fetchMonthSummaries } from '@/redux/reporting/reporting';
import {
  Analytics,
  registerCloseNotificationBanner,
} from '@/services/analytics';
import { requestVersionControl } from '@/services/api/version';
import { setClaritySessionId, setClarityUserId } from '@/services/clarity';
import { fileOpenerService } from '@/services/fileOpener';
import InsightService from '@/services/insights';
import { storageService } from '@/services/storage';
import { RootState } from '@/store';
import { retrieveCommercialOffers } from '@/utils/commercialOfferHelper';
import {
  DateRangeErrorType,
  getDateRangeError,
  getEffectiveDateRange,
  getMinAllowedDate,
  getRangeTooWideTitle,
  getSearchScopePeriod,
  hasSpecificFilter,
  MAX_DATE_RANGE_DAYS,
} from '@/utils/dashboardDateRange';
import { pricesHelper } from '@/utils/pricesHelper';

import { useAlertHandlersActions } from './AlertsHandlersActions';
import styles from './OperationsDashboard.module.scss';

const URL_CEX = ENVIRONMENT.URL_PEPPERONE;
const DATE_RANGE_ERROR_ID = 'dashboard-date-range-error';
const DATE_RANGE_INLINE_ERROR_ID = 'dashboard-date-range-inline-error';
const MIN_SEARCH_LENGTH = 4;
const NAME_OR_DNI_LENGTH_ERROR_ID = 'filter-name-or-dni-length-error';
const PROPOSAL_REF_LENGTH_ERROR_ID = 'filter-proposal-reference-length-error';

// Hay texto, pero no el suficiente para lanzar la búsqueda
const isSearchTooShort = (value?: string | null) => {
  const { length } = (value ?? '').trim();
  return length > 0 && length < MIN_SEARCH_LENGTH;
};

let abortController = new AbortController();

interface typesFilters {
  offset: number;
  limit: 20;
  nameOrDni: string;
  cart_id: string;
  from_date: string;
  to_date: string;
  buttons: string;
  proposal_reference: string;
}

export default function OperationsDashboard() {
  const isTabletMini = useMediaQuery(Breakpoint.TabletMini);
  const isDesktop = useMediaQuery(Breakpoint.Desktop);

  const statusFilters = useSelector((state: RootState) => state.filters.data);
  const nameOrDniFilter = useSelector(
    (state: RootState) => state.filters.nameOrDni,
  );
  const cartIdFilter = useSelector((state: RootState) => state.filters.cartId);
  const startDateFilter = useSelector(
    (state: RootState) => state.filters.startDate,
  );
  const endDateFilter = useSelector(
    (state: RootState) => state.filters.endDate,
  );
  const proposalRefFilter = useSelector(
    (state: RootState) => state.filters.proposalReference,
  );
  const ecommercePlatform = useSelector(
    (state: RootState) => state.ecommercePlatform.data,
  );
  const versionControlData = useSelector(
    (state: RootState) => state.configSlice.versionControl,
  );
  const isOperationUpdate = useSelector(
    (state: RootState) => state.operation.updateAvailable,
  );
  const formatSelectedInitials = (selectedInitials: string[]): string => (selectedInitials[0] !== 'ALL'
    ? selectedInitials
      .map((initials) => `${'&'}business_status=${initials}`)
      .join('')
    : '');

  const isNameOrDni = (value: string) => {
    const name = value.trim();
    if (!name) {
      return '';
    }
    if (/\d/.test(name)) {
      return `&document_id=${name}`;
    }
    if (/^[A-Za-z\s]+$/.test(name)) {
      return `&client_name=${name}`;
    }
    return '';
  };

  const isCartId = (value: string) => {
    const name = value.trim();
    if (name.length > 0) {
      return `&cart_id=${name}`;
    }
    return '';
  };

  const isProposalReference = (value: string) => {
    const name = value.trim();
    if (name.length > 0) {
      return `&proposal_reference=${name}`;
    }
    return '';
  };

  const [appVersion, setAppVersion] = useState('');

  // banner oculto al hacer scroll
  const [showBanner, setShowBanner] = useState(true);
  // const [isTransitioning, setIsTransitioning] = useState(false);
  const lastScrollTop = useRef(0);

  const [showAGCodeFilter, setShowAGCodeFilter] = useState(false);
  // Persistencia por sesión: si el usuario ya cerró el banner antes en esta
  // sesión, arrancamos con dismissed=true para que no vuelva a aparecer.
  const [alertDismissed, setAlertDismissed] = useState(
    sessionStorage.getItem('Pepper-AlertDismissed') === 'true',
  );

  if (!isPlatform('mobileweb') && !isPlatform('desktop')) {
    App?.getInfo().then((version) => {
      setAppVersion(version.version);
    });
  }

  const { presentModal: presentOperationsFilterModal } = useModal(
    OperationsFiltersModal,
  );

  const [buttons, setButtons] = useState<Status[]>(statusFilters);
  const [showNotifModal, setShowNotifModal] = useState<boolean>(false);
  const [merchantData, setMerchantData] = useState<Merchant>();
  const [dashboardData, setDashboardData] = useState<Dashboard[]>([]);

  const [unfilteredOperations, setUnfilteredOperations] = useState<Dashboard[]>(
    [],
  );
  const [open, setOpen] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [commercialOffers, setCommercialOffers] = useState<boolean>();
  const [ecoCommercialOffer, setEcoCommercialOffer] = useState<boolean>();
  const [commercialOffersRetrieved, setCommercialOffersRetrieved] = useState<
  CommercialOfferSimulations[]
  >([]);
  const [searchName, setSearchName] = useState<string>(nameOrDniFilter);
  const [searchCartID, setSearchCartID] = useState<string | null | undefined>(
    cartIdFilter,
  );
  const [searchProposalRef, setSearchProposalRef] = useState<
  string | null | undefined
  >(proposalRefFilter);
  const [startDate, setStartDate] = useState<string>(startDateFilter);
  const [endDate, setEndDate] = useState<string>(endDateFilter);
  const [cexUrl, setCexUrl] = useState('');
  const location = useLocation();
  const dispatch = useDispatch();
  const contentRef = useRef<HTMLIonContentElement | null>(null);
  const datePickerRef = useRef<SearchDatePickerRef>(null);
  const merchantInfo = useSelector(
    (state: RootState) => state.merchantData.data,
  );
  const filtersInitData: typesFilters = {
    offset: 0,
    limit: 20,
    nameOrDni: isNameOrDni(nameOrDniFilter),
    cart_id: isCartId(cartIdFilter),
    proposal_reference: isProposalReference(proposalRefFilter),
    from_date: startDateFilter
      ? `${FilterDashboard.FromDate}${startDateFilter}`
      : '',
    to_date: endDateFilter ? `${FilterDashboard.ToDate}${endDateFilter}` : '',
    buttons: formatSelectedInitials(
      statusFilters
        .filter((button) => button.isSelected)
        .map((button) => button.initials),
    ),
  };
  const customDashboardColumns = useSelector(
    (state: RootState) => state.dashboard.columns,
  );

  const [filterValues, setFilterValues] = useState<typesFilters>(filtersInitData);
  const [apiErrorMessage, setApiErrorMessage] = useState<string | null>(null);
  const [nameOrDniTooShort, setNameOrDniTooShort] = useState(false);
  const [proposalRefTooShort, setProposalRefTooShort] = useState(false);

  const prevfilterValuesRef = useRef<typesFilters>(filtersInitData);

  const [orderBy, setOrderBy] = useState<OrderSortType>({
    order: OrderType.DATE,
    sort: SortType.DESC,
  });

  const [loadingData, setloadingData] = useState(true);

  const [cleared, setCleared] = useState<boolean>(true);
  const isDesktopLarge = useMediaQuery(Breakpoint.DesktopLarge);
  const isTablet = useMediaQuery(Breakpoint.Tablet);
  const [moreItems, setMoreItems] = useState<boolean>(true);
  const [cookieProposalReference, setCookieProposalReference] = useState(
    storageService.hasAGCode(),
  );
  const [superExpressFlow] = useState(storageService.hasSuperExpress);
  const [versionControl, setVersionControl] = useState<VersionControl>();
  const storedMonthSummaries = useSelector(
    (state: RootState) => state.settlement.data.monthSummaries,
  );
  const [currentMonthSummary, setCurrentMonthSummary] = useState<
  MonthSummary | undefined
  >();
  const [isNFCSupported, setIsNFCSupported] = useState(false);
  const [isRefreshing] = useState(false);

  const dashboardApiCalling = useRef<boolean>(false);

  const { presentModal: presentEDniModal, dismissModal: dismissEDniModal } = useModal(EDniModal);
  const {
    presentModal: presentManagedModal,
    dismissModal: dismissManagedModal,
  } = useModal(OperationManged);

  useEffect(() => {
    const newValue = storageService.hasAGCode();
    if (cookieProposalReference !== newValue) {
      setCookieProposalReference(newValue);
    }
  }, [storageService.hasAGCode()]);

  useEffect(() => {
    const handleScroll = (event: CustomEvent) => {
      const { scrollTop } = event.detail;
      if (scrollTop === 0) {
        // Scroll hasta arriba
        setShowBanner(true);
        //     setIsTransitioning(true);
      } else if (scrollTop > lastScrollTop.current) {
        // Scroll hacia abajo
        setShowBanner(false);
        //    setIsTransitioning(true);
      }
      lastScrollTop.current = scrollTop <= 0 ? 0 : scrollTop; // Para evitar valores negativos
    };

    const contentElement = contentRef.current;
    contentElement?.addEventListener('ionScroll', handleScroll);

    return () => {
      contentElement?.removeEventListener('ionScroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    setCurrentMonthSummary(storedMonthSummaries[0]);
    if (storedMonthSummaries.length > 0) {
      Analytics.setAnalyticsUserProperties({
        [ClientTaxonomyProps.MTD_ACT_AMOUNT_SUM]:
          storedMonthSummaries[0].total_amount.toString(),
        [ClientTaxonomyProps.MTD_ACT_LOANS_COUNT]:
          storedMonthSummaries[0].total_loans.toString(),
        [ClientTaxonomyProps.LM_ACT_AMOUNT_SUM]:
          storedMonthSummaries[1].total_amount.toString(),
        [ClientTaxonomyProps.LM_ACT_LOANS_COUNT]:
          storedMonthSummaries[1].total_loans.toString(),
      });
    }
  }, [storedMonthSummaries]);

  useEffect(() => {
    if (DniReader && Capacitor.isNativePlatform() && isPlatform('android')) {
      DniReader.isSupported().then((result) => {
        setIsNFCSupported(result.isSupported);
      });
    } else if (isPlatform('ios')) {
      idniecap.isNFCEnable().then((result) => {
        if (result.disponible) {
          setIsNFCSupported(true);
        }
      });
    }
    if (storedMonthSummaries.length === 0) {
      dispatch(fetchMonthSummaries() as unknown as AnyAction);
    }
  }, []);

  useEffect(() => {
    if (ecoCommercialOffer !== undefined) {
      const shouldShowCartIDFilter = ecoCommercialOffer;

      if (!shouldShowCartIDFilter) {
        if (filterValues.cart_id !== '') {
          setFilterValues({
            ...filterValues,
            cart_id: '',
          });
          dispatch(saveCartId(''));
          searchCartID !== '' && setSearchCartID('');
        }
      }
    } else if (
      !customDashboardColumns.find((column) => column?.toUpperCase().includes(DashboardColumns.CART_ID))
      && (isDesktopLarge || isTablet)
    ) {
      if (filterValues.cart_id !== '') {
        setFilterValues({
          ...filterValues,
          cart_id: '',
        });
        dispatch(saveCartId(''));
        searchCartID !== '' && setSearchCartID('');
      }
    }
  }, [customDashboardColumns, isDesktopLarge, ecoCommercialOffer]);

  const getDateRangeValidation = (filters: typesFilters) => {
    const {
      FromDate, ToDate, DocumentId, ClientName, CartId, ProposalReference,
    } = FilterDashboard;
    const dateRange = {
      fromDate: filters.from_date.replace(FromDate, ''),
      toDate: filters.to_date.replace(ToDate, ''),
    };
    const specificFilter = hasSpecificFilter({
      nameOrDni: filters.nameOrDni.replace(DocumentId, '').replace(ClientName, ''),
      cartId: filters.cart_id.replace(CartId, ''),
      proposalReference: cookieProposalReference
        ? filters.proposal_reference.replace(ProposalReference, '')
        : '',
    });
    return {
      dateRange,
      specificFilter,
      error: getDateRangeError(dateRange, specificFilter),
    };
  };

  const dashboardApiCall = async (
    multipleFilters: typesFilters,
    hideLoader?: boolean,
  ) => {
    const { dateRange, specificFilter, error } = getDateRangeValidation(multipleFilters);
    if (error) {
      // No se lanza la búsqueda hasta que el usuario corrija las fechas
      abortController.abort();
      dashboardApiCalling.current = false;
      setloadingData(false);
      setMoreItems(false);
      return;
    }
    const { FromDate, ToDate } = FilterDashboard;
    const effectiveRange = getEffectiveDateRange(dateRange, specificFilter);
    try {
      setApiErrorMessage(null);
      dashboardApiCalling.current = true;
      if (hideLoader) {
        setloadingData(false);
        setMoreItems(false);
      } else {
        setloadingData(true);
        setMoreItems(true);
      }
      abortController.abort();
      abortController = new AbortController();
      if (!cookieProposalReference) {
        multipleFilters.proposal_reference = '';
        dispatch(saveProposalRef(''));
      }
      const dashboardApiResponse = await getDashboards(
        {
          ...multipleFilters,
          from_date: `${FromDate}${effectiveRange.fromDate}`,
          to_date: `${ToDate}${effectiveRange.toDate}`,
        },
        orderBy,
        { signal: abortController.signal },
      );
      const dashboardResponse: Dashboards = dashboardApiResponse.data;

      if (dashboardApiResponse.metadata) {
        setMoreItems(dashboardApiResponse.metadata?.paging.more_items);
      }

      dashboardResponse
        && setDashboardData((prevData: any) => [
          ...prevData,
          ...dashboardResponse.dashboards,
        ]);
      dispatch(updateOperationStatus(false));
      dashboardApiCalling.current = false;
      setloadingData(false);
      return dashboardApiResponse;
    } catch (err: any) {
      if (err.code !== 'ERR_CANCELED') {
        setloadingData(false);
        setApiErrorMessage(err?.result?.errors?.[0]?.message ?? null);
      }
    }
  };

  const merchantAndCommercialApiCall = async () => {
    dispatch(clearIsPreferredOffer());
    try {
      const token = sessionStorage.getItem('accessToken');
      let merchantStored = null;
      if (merchantInfo) {
        merchantStored = merchantInfo;
      } else {
        merchantStored = await getMerchants();
        sessionStorage.setItem(
          'Pepper-MerchantData',
          JSON.stringify(merchantStored),
        );
        dispatch(saveMerchantData(merchantStored));
      }
      Analytics.setAnalyticsUserProperties({
        [ClientTaxonomyProps.USER_ID]: merchantStored.merchant_id,
        [ClientTaxonomyProps.SESSION_ID]: `${merchantStored.merchant_id}_${dayjs().format('YYYYMMDD')}_${token}`,
      });
      setClarityUserId(merchantStored.merchant_id);
      setClaritySessionId(
        `${merchantStored.merchant_id}_${dayjs().format('YYYYMMDD')}_${token}`,
      );

      setMerchantData(merchantStored);
      if (merchantStored.level === 3) {
        // Si es nivel 3, se comprueban las ofertas comerciales
        const offerS = sessionStorage.getItem('pepper-commercial-offers-S');
        let commercialOffers: FlowCommercialOffer[] | null = null;
        // Si no hay ofertas comerciales en sesión, se consultan
        if (!offerS) {
          // Se consultan las ofertas comerciales
          commercialOffers = await retrieveCommercialOffers();
          if (commercialOffers) {
            // Se guardan en sesión
            sessionStorage.setItem(
              'pepper-commercial-offers-S',
              JSON.stringify(commercialOffers),
            );
          }
        } else {
          commercialOffers = JSON.parse(offerS);
        }
        if (commercialOffers && commercialOffers?.length > 0) {
          commercialOffers.find(
            (offer: FlowCommercialOffer) => offer.flow === FlowType.CEX
              || offer.flow === FlowType.PLS
              || offer.flow === FlowType.SUS,
          ) && dispatch(existingCommercialOffers(true));
          const commercialOffersSelection: any[] = [];
          commercialOffers.forEach(async (offer: FlowCommercialOffer) => {
            if (offer.commercial_offers.length > 0) {
              const key = `pepperCommercialOffers${offer.flow}`;
              sessionStorage.setItem(key, 'true');
              if (offer.flow === FlowType.ECO) {
                setEcoCommercialOffer(true);
                dispatch(existingEcoOffers(true));
                setCommercialOffers(true);
                dispatch(checkmanagedOffers(true));
                const preferredOffer = offer.commercial_offers.some(
                  (commercialOffer: CommercialOffer) => commercialOffer.is_preferred,
                );
                const noPreferredOffer = offer.commercial_offers.some(
                  (commercialOffer: CommercialOffer) => !commercialOffer.is_preferred,
                );
                dispatch(
                  checkpreferredOfferAndNoPreferredOffer(
                    preferredOffer && noPreferredOffer,
                  ),
                );
                dispatch(
                  checkValueShowSelect(preferredOffer && noPreferredOffer),
                );
                // Si no hay las dos, entonces guarda si hay preferred offer o no preferred offer
                if (!(preferredOffer && noPreferredOffer)) {
                  if (preferredOffer) {
                    dispatch(saveIsPreferredOffer(true));
                  } else {
                    dispatch(saveIsPreferredOffer(false));
                  }
                }

                // comprobación cart_id
                // const nonEcoFlows = commercialOffers!.filter(
                // (flow: FlowCommercialOffer) => flow.flow !== FlowType.ECO,
                // );
                // if (nonEcoFlows.length === 0) {
                let responseEcommercePlatform;
                if (!ecommercePlatform) {
                  const response = await getPlatform();
                  if (response) {
                    dispatch(saveEcommercePlatformData(response));
                    responseEcommercePlatform = response;
                  }
                } else {
                  responseEcommercePlatform = ecommercePlatform;
                }
                const cart_data = responseEcommercePlatform?.data?.cart_uri;
                if (cart_data) {
                  dispatch(existingCartId(true));
                } else {
                  dispatch(existingCartId(false));
                }
                //  }
                // ------
              } else {
                setEcoCommercialOffer(false);
                setCommercialOffers(true);
                offer.commercial_offers.forEach(
                  (commercialOffer: CommercialOffer) => {
                    commercialOffersSelection.push(commercialOffer);
                  },
                );
              }
            }
          });
          commercialOffersSelection.length > 0
            && saveTermsAvailable(commercialOffersSelection);
        }
      }
    } catch (err: any) {
      console.log(err);
    }
  };

  const saveTermsAvailable = (commercialOffersFound: CommercialOffer[]) => {
    const commercialOffersFiltered: CommercialOfferSimulations[] = [];
    commercialOffersFound.forEach((offer: CommercialOffer) => {
      commercialOffersFiltered.push({
        max_amount: offer.max_amount,
        min_amount: offer.min_amount,
        term: offer.term,
      });
    });
    setCommercialOffersRetrieved(commercialOffersFiltered);
    sessionStorage.setItem(
      'pepperSimulationCommercialOffers',
      JSON.stringify(commercialOffersFiltered),
    );
  };

  const deleteFilterByNameOrDni = () => {
    setDashboardData([]);
    setCurrentPage(0);
    dispatch(saveNameOrDni(''));
    return setFilterValues((prevValues: any) => ({
      ...prevValues,
      nameOrDni: '',
      offset: 0,
    }));
  };

  const deleteFilterByCartId = () => {
    setDashboardData([]);
    setCurrentPage(0);
    dispatch(saveCartId(''));
    return setFilterValues((prevValues: any) => ({
      ...prevValues,
      cart_id: '',
      offset: 0,
    }));
  };

  const deleteFilterByProposalRef = () => {
    setDashboardData([]);
    setCurrentPage(0);
    dispatch(saveProposalRef(''));
    return setFilterValues((prevValues: any) => ({
      ...prevValues,
      proposal_reference: '',
      offset: 0,
    }));
  };

  const deleteAllFiltersInputs = () => {
    setSearchName('');
    setSearchCartID('');
    setSearchProposalRef('');
    setStartDate('');
    setEndDate('');
    setCleared(false);
    setNameOrDniTooShort(false);
    setProposalRefTooShort(false);

    dispatch(saveNameOrDni(''));
    dispatch(saveCartId(''));
    dispatch(saveProposalRef(''));
    dispatch(saveFilters([]));
    setFilterValues({
      ...filterValues,
      proposal_reference: '',
      nameOrDni: '',
      cart_id: '',
      buttons: '',
    });
  };

  const checkNameOrDni = (value: string) => {
    const { DocumentId, ClientName } = FilterDashboard;
    const data = value.trim();
    setNameOrDniTooShort(isSearchTooShort(data));
    const filterSelected = filterValues.nameOrDni.replace(
      ClientName || DocumentId,
      '',
    );
    if (data.length > 3 && filterSelected !== data) {
      dispatch(saveNameOrDni(data));
      if (data !== nameOrDniFilter) {
        if (/\d/.test(data)) {
          setDashboardData([]);
          setCurrentPage(0);
          return setFilterValues((prevValues: any) => ({
            ...prevValues,
            nameOrDni: `${DocumentId}${data}`,
            offset: 0,
          }));
        }
        if (/^[A-Za-z\s]+$/.test(data)) {
          setDashboardData([]);
          setCurrentPage(0);
          return setFilterValues((prevValues: any) => ({
            ...prevValues,
            nameOrDni: `${ClientName}${data}`,
            offset: 0,
          }));
        }
      }
    }
  };

  const handleCartID = async (search: any) => {
    const { CartId } = FilterDashboard;
    const value = (search ?? '').trim();

    if (value.length > 0) {
      setDashboardData([]);
      setFilterValues((prevValues: any) => ({
        ...prevValues,
        cart_id: `${CartId}${value}`,
        offset: 0,
      }));
      setCurrentPage(0);
    } else if (filterValues.cart_id) {
      deleteFilterByCartId();
    }
    dispatch(saveCartId(value));
  };

  const handleProposalRef = async (search: any) => {
    const { ProposalReference } = FilterDashboard;
    const value = (search ?? '').trim();
    setProposalRefTooShort(isSearchTooShort(value));

    if (value.length > 3) {
      setDashboardData([]);
      setFilterValues((prevValues: any) => ({
        ...prevValues,
        proposal_reference: `${ProposalReference}${value}`,
        offset: 0,
      }));
      setCurrentPage(0);
    } else if (value.length === 0 && filterValues.proposal_reference) {
      deleteFilterByProposalRef();
    }
    dispatch(saveProposalRef(value));
  };

  /*   const openModal = (open: boolean) => {
      setModalOpen(open);
    };
   */
  const updateFilterValues = (newFilterValues: any) => {
    setDashboardData([]);
    setCurrentPage(0);
    setFilterValues((prevFilterValues) => ({
      ...prevFilterValues,
      ...newFilterValues,
      offset: 0,
    }));
  };

  const updateButtonsWithSelectedStatus = (
    buttons: Status[],
    initials: InitialStatus,
    isSelected: boolean,
  ): Status[] => buttons.map((button) => ({
    ...button,
    isSelected:
        button.initials === initials
          ? isSelected
          : button.initials === InitialStatus.Todas
            ? false
            : button.isSelected,
  }));

  const selectButtons = async (
    initials: InitialStatus,
    isSelected: boolean,
  ) => {
    let updatedButtons: Status[] = [];
    if (initials === InitialStatus.Todas) {
      updatedButtons = buttons.map((button) => ({
        ...button,
        isSelected: button.initials === initials,
      }));
      setCurrentPage(0);
      deleteAllFiltersInputs();
    } else {
      updatedButtons = updateButtonsWithSelectedStatus(
        buttons,
        initials,
        isSelected,
      );
      const selectedInitials = updatedButtons
        .filter((button) => button.isSelected)
        .map((button) => button.initials);

      if (selectedInitials.length === 0) {
        updatedButtons = buttons.map((button) => ({
          ...button,
          isSelected: button.initials === InitialStatus.Todas,
        }));
      }
      const formattedInitials = formatSelectedInitials(selectedInitials);
      setFilterValues((prevValues) => ({
        ...prevValues,
        buttons: formattedInitials,
        offset: 0,
      }));
      setCurrentPage(0);
    }
    setButtons(updatedButtons);
    dispatch(saveFilters(updatedButtons));
  };

  const selectOnlyStatus = (initials: InitialStatus) => {
    const updatedButtons = buttons.map((button) => ({
      ...button,
      isSelected: button.initials === initials,
    }));

    setButtons(updatedButtons);
    setFilterValues((prevValues) => ({
      ...prevValues,
      buttons: formatSelectedInitials([initials]),
      offset: 0,
    }));
    setCurrentPage(0);
    dispatch(saveFilters(updatedButtons));
  };

  const calendar = () => {
    setCleared(true);
    const { FromDate, ToDate } = FilterDashboard;
    if (
      startDate !== '1970-01-01'
      && endDate !== '1970-01-01'
      && startDate !== null
      && endDate !== null
      && startDate !== ''
      && endDate !== ''
    ) {
      setDashboardData([]);
      setFilterValues((prevValues: any) => ({
        ...prevValues,
        from_date: `${FromDate}${startDate}`,
        to_date: `${ToDate}${endDate}`,
        offset: 0,
      }));
      setCurrentPage(0);
    } else if (
      (startDate !== '1970-01-01' && startDate !== null && startDate !== '')
      || (endDate !== '1970-01-01' && endDate !== null && endDate !== '')
    ) {
      // TODO: Falta comprobar su utilidad
    } else {
      setFilterValues((prevValues: any) => ({
        ...prevValues,
        from_date: '',
        to_date: '',
        offset: 0,
      }));
      setCurrentPage(0);
    }
  };

  const searchNext = async () => {
    setloadingData(true);
    const newOffset = currentPage + 20;
    const newFilterValues = {
      ...filterValues,
      offset: newOffset,
    };
    setCurrentPage(newOffset);
    // setFilterValues(newFilterValues);
    await dashboardApiCall(newFilterValues);
    setloadingData(false);
  };

  const handleOrderByData = (data: OrderSortType) => {
    setDashboardData([]);
    const newFilterValues = {
      ...filterValues,
      offset: 0,
    };
    setCurrentPage(0);
    setFilterValues(newFilterValues);
    setOrderBy(data);
  };

  const listener = (event: any) => {
    if (
      event.origin === ENVIRONMENT.URL_PEPPERONE
      && event.data?.message === 'close CEX'
    ) {
      setModalOpen(false);
      dismissEDniModal();
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.set('reload', 'true');
      setDashboardData([]);
      dashboardApiCall(filtersInitData);
      window.removeEventListener('message', listener);
    } else if (
      event.origin === ENVIRONMENT.URL_PEPPERONE
      && event.data?.message === 'Pepper - Signature Contract'
    ) {
      fileOpenerService.openContract(event.data.contractString);
    }
  };

  useEffect(
    () => () => {
      window.removeEventListener('message', listener);
    },
    [],
  );
  const allowedOrigins = [
    ENVIRONMENT.URL_ECOMMERCE,
    ENVIRONMENT.URL_ECOMMERCE_2,
    ENVIRONMENT.URL_ECO,
    ENVIRONMENT.URL_ECO_2,
  ];
  const listenerManaged = (event: any) => {
    if (
      allowedOrigins.includes(event.origin)
      && event.data?.message === 'close iframe'
    ) {
      dismissManagedModal();
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.set('reload', 'true');
      setDashboardData([]);
      dashboardApiCall(filtersInitData);
      window.removeEventListener('message', listenerManaged);
    } else if (
      allowedOrigins.includes(event.origin)
      && event.data?.message === 'Pepper - Signature Contract'
    ) {
      fileOpenerService.openContract(event.data.contractString);
    }
  };

  useEffect(
    () => () => {
      window.removeEventListener('message', listenerManaged);
    },
    [],
  );

  const getLead = async (): Promise<string | undefined> => {
    try {
      const leadsResponse = await postLeads();
      sessionStorage.setItem('leadToken', leadsResponse?.data.token);
      if (leadsResponse) {
        InsightService.logTrace('Pepper One LeadToken', [
          { Scope: 'PEPPER_ONE' },
          { LeadToken: leadsResponse?.data.token },
        ]);
      }
      const expirationDate = new Date();
      expirationDate.setSeconds(
        expirationDate.getSeconds() + leadsResponse?.data.expires_in,
      );
      sessionStorage.setItem('expiresLeads', expirationDate.toUTCString());
      return leadsResponse?.data.token;
    } catch (err) {
      dispatch(addError(''));
    }
  };

  const handleNewOperation = async () => {
    const lead_token = sessionStorage.getItem('leadToken');
    const lead_token_expiration = sessionStorage.getItem('expiresLeads');
    const currentTime = new Date();
    let url = '';
    if (
      lead_token
      && lead_token_expiration
      && new Date(lead_token_expiration) > currentTime
    ) {
      setCexUrl(
        `${URL_CEX}/?lead_token=${lead_token}&shared_offers=true${Capacitor.isNativePlatform() ? '&is_app=true' : ''}`,
      );
      setModalOpen(true);
      window.addEventListener('message', listener, false);
      const commercialOffersStored = sessionStorage.getItem(
        'pepperSimulationCommercialOffers',
      );
      commercialOffersRetrieved.length === 0
        && commercialOffersStored
        && setCommercialOffersRetrieved(JSON.parse(commercialOffersStored));
    } else {
      const tokenResponse = await getLead();
      if (tokenResponse) {
        url = `${URL_CEX}/?lead_token=${tokenResponse}&shared_offers=true${Capacitor.isNativePlatform() ? '&is_app=true' : ''}`;
        setCexUrl(url);
        const commercialOffersStored = sessionStorage.getItem(
          'pepperSimulationCommercialOffers',
        );
        commercialOffersRetrieved.length === 0
          && commercialOffersStored
          && setCommercialOffersRetrieved(JSON.parse(commercialOffersStored));
        setModalOpen(true);
        window.addEventListener('message', listener, false);
      } else {
        dispatch(addError(''));
      }
    }
    Analytics.registerAnalyticsEvent({
      [ClientTaxonomyEvents.OPEN_PEPPERONE]: { type: 'NEW' },
    });
  };

  const handleNewDniEOperation = async () => {
    Analytics.registerAnalyticsEvent({
      [ClientTaxonomyEvents.OPEN_PEPPERONE]: { type: 'SUPEREXPRESS' },
    });
    window.addEventListener('message', listener, false);
    presentEDniModal({
      backdropDismiss: false,
      centered: true,
      useCanDismiss: true,
      onDismiss: () => {
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.set('reload', 'true');
        setDashboardData([]);
        dashboardApiCall(filtersInitData);
      },
    });
    setTimeout(() => {
      setDashboardData([]);
    }, 1000);
    const lead_token = sessionStorage.getItem('leadToken');
    const lead_token_expiration = sessionStorage.getItem('expiresLeads');
    const currentTime = new Date();
    if (
      !lead_token
      || !lead_token_expiration
      || (lead_token
        && lead_token_expiration
        && new Date(lead_token_expiration) < currentTime)
    ) {
      await getLead();
    }
  };

  const handleNewOperationManaged = async () => {
    merchantAndCommercialApiCall();
    window.addEventListener('message', listenerManaged);
    presentManagedModal({
      backdropDismiss: false,
      centered: true,
      useCanDismiss: true,
      managed: true,
      onDismiss: () => {
        // window.removeEventListener('message', listenerManaged);
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.set('reload', 'true');
        setDashboardData([]);
        dashboardApiCall(filtersInitData);
      },
    });
    sessionStorage.removeItem('retaken-eco');
    setTimeout(() => {
      setDashboardData([]);
    }, 1000);
    const lead_token = sessionStorage.getItem('leadToken');
    const lead_token_expiration = sessionStorage.getItem('expiresLeads');
    const currentTime = new Date();
    if (
      !lead_token
      || !lead_token_expiration
      || (lead_token
        && lead_token_expiration
        && new Date(lead_token_expiration) < currentTime)
    ) {
      await getLead();
    }
  };

  const sendTermsData = () => {
    const pepperOneIframe = document.getElementById(
      'pepper-one-iframe',
    ) as HTMLIFrameElement;
    if (pepperOneIframe?.contentWindow) {
      pepperOneIframe.contentWindow.postMessage(
        {
          simulationCommercialOffers: commercialOffersRetrieved,
        },
        '*',
      );
    }
  };

  // Backup for dashboardApiCall if it's not fired after returning from OperationDetail
  useIonViewDidEnter(() => {
    if (isOperationUpdate && !dashboardApiCalling.current) {
      dashboardApiCall(filterValues);
    }
  }, [isOperationUpdate]);

  useEffect(() => {
    !modalOpen && setCexUrl('');
  }, [modalOpen]);

  useEffect(() => {
    if (
      prevfilterValuesRef.current !== filterValues
      || filterValues.proposal_reference === ''
    ) {
      setloadingData(true);
      setDashboardData([]);
      dashboardApiCall(filterValues);
    }
    prevfilterValuesRef.current = filterValues;
  }, [filterValues, orderBy]);


  useEffect(() => {
    let cancelled = false;
    const defaultRange = getEffectiveDateRange({ fromDate: '', toDate: '' }, false);
    const alertFilters: typesFilters = {
      offset: 0,
      limit: 20,
      nameOrDni: '',
      cart_id: '',
      proposal_reference: '',
      from_date: `${FilterDashboard.FromDate}${defaultRange.fromDate}`,
      to_date: `${FilterDashboard.ToDate}${defaultRange.toDate}`,
      buttons: '',
    };
    getDashboards(alertFilters, { order: OrderType.DATE, sort: SortType.DESC })
      .then((res) => {
        if (!cancelled && res?.data?.dashboards) {
          setUnfilteredOperations(res.data.dashboards);
        }
      })
      .catch(() => {
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    dispatch(saveStartDate(startDate));
    dispatch(saveEndDate(endDate));
    if (startDate !== startDateFilter || endDate !== endDateFilter) {
      if (startDate || endDate) {
        calendar();
      } else if (startDate === '' || endDate === '') {
        setDashboardData([]);
        setFilterValues((prevValues: any) => ({
          ...prevValues,
          from_date: '',
          to_date: '',
          offset: 0,
        }));
        setCurrentPage(0);
      }
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!cookieProposalReference && filterValues.proposal_reference !== '') {
      setFilterValues((prevValues: any) => ({
        ...prevValues,
        proposal_reference: '',
        offset: 0,
      }));
      dispatch(saveProposalRef(''));
    }
  }, [cookieProposalReference]);

  const checkDefaultValues = async () => {
    let superExpressValue = false;
    if (!versionControlData) {
      await requestVersionControl().then((res) => {
        // If it's a native platform, we take the value from versionControl file
        // Else, the superExpress value is false
        superExpressValue = Capacitor.isNativePlatform()
          ? (res as VersionControl).super_express_default
          : false;
        const versionRes = {
          ...(res as VersionControl),
          super_express_default: superExpressValue,
        };
        setVersionControl(versionRes);
        dispatch(saveVersionControl(versionRes));
      });
    } else {
      setVersionControl(versionControlData);
      superExpressValue = Capacitor.isNativePlatform()
        ? versionControlData.super_express_default
        : false;
    }
    Analytics.setAnalyticsUserProperties({
      [ClientTaxonomyProps.SUPER_EXPRESS_ENABLED]: (superExpressFlow !== null
        ? superExpressFlow
        : superExpressValue
      ).toString(),
    });
  };

  useEffect(() => {
    merchantAndCommercialApiCall();
    !(filterValues.proposal_reference !== '' && !cookieProposalReference)
      && dashboardApiCall(filterValues);

    if (sessionStorage.getItem('notificationAdvise') !== 'true') {
      sessionStorage.setItem('notificationAdvise', 'true');
      setShowNotifModal(true);
    } else {
      setShowNotifModal(false);
    }
    checkDefaultValues();
    Analytics.setAnalyticsUserProperties({
      [ClientTaxonomyProps.AG_CODE_ENABLED]:
        (!!cookieProposalReference).toString(),
    });
  }, []);

  const openDatePicker = () => {
    datePickerRef.current?.openDatePicker();
  };

  const {
    dateRange: appliedDateRange,
    specificFilter: appliedSpecificFilter,
    error: dateRangeError,
  } = getDateRangeValidation(filterValues);
  const showCartIdFilter = !!ecoCommercialOffer
    || customDashboardColumns.some(
      (column) => column?.toUpperCase().includes(DashboardColumns.CART_ID)
        && (isDesktopLarge || isTablet),
    );
  const availableSpecificFilters = {
    cartId: showCartIdFilter,
    proposalReference: showAGCodeFilter && !!cookieProposalReference,
  };

  // El error de intervalo demasiado amplio se muestra en el estado vacío de la tabla
  const isRangeTooWide = dateRangeError?.type === DateRangeErrorType.RangeTooWide;
  const filtersErrorMessage = isRangeTooWide
    ? null
    : dateRangeError?.message ?? apiErrorMessage;
  const dateRangeErrorIds = isRangeTooWide
    ? `${DATE_RANGE_INLINE_ERROR_ID} ${DATE_RANGE_ERROR_ID}`
    : (dateRangeError && DATE_RANGE_ERROR_ID) || undefined;
  const showSearchScope = !dateRangeError
    && !apiErrorMessage
    && !appliedDateRange.fromDate
    && !appliedDateRange.toDate;
  const searchScopePeriod = getSearchScopePeriod(appliedSpecificFilter);
  // La API no devuelve el total: solo se muestra cuando ya están todas cargadas
  const showResultsCount = appliedSpecificFilter
    && !loadingData
    && !moreItems
    && dashboardData.length > 0;

  const renderSearchLengthError = (id: string) => (
    <IonText id={id} role="alert" className={styles.searchFieldError}>
      {`Introduce al menos ${MIN_SEARCH_LENGTH} caracteres para buscar.`}
    </IonText>
  );

  const renderFilters = (mobile = false) => (
    <div className={classNames(styles.filters, mobile && styles.mobile)}>
      <div className={styles.filterNameDni}>
        <div className={styles.searchField}>
          <div className={styles.searchInputContainer}>
            <IonIcon
              md={searchOutline}
              className={styles.iconSearch}
              onClick={() => checkNameOrDni(searchName)}
            />
            <IonInput
              className={styles.inputSearch}
              id="filterNameOrDni"
              minlength={MIN_SEARCH_LENGTH}
              clearInput
              placeholder="Buscar por nombre o DNI/CIF"
              aria-invalid={nameOrDniTooShort || undefined}
              aria-describedby={nameOrDniTooShort ? NAME_OR_DNI_LENGTH_ERROR_ID : undefined}
              onIonInput={(event: CustomEvent<InputChangeEventDetail>) => {
                setSearchName(event.detail.value ?? '');
                setNameOrDniTooShort(false);
                !event.detail.value && deleteFilterByNameOrDni();
              }}
              onIonBlur={() => {
                checkNameOrDni(searchName);
              }}
              onKeyUp={(event: any) => {
                if (event.key === 'Enter') {
                  checkNameOrDni(searchName);
                }
              }}
              value={searchName}
            />
          </div>
          {nameOrDniTooShort && renderSearchLengthError(NAME_OR_DNI_LENGTH_ERROR_ID)}
        </div>
        {showCartIdFilter && (
          <>
            <div className={styles.inputSeparator} />
            <div className={styles.searchField}>
              <div className={styles.searchInputContainer}>
                <IonIcon
                  md={searchOutline}
                  className={styles.iconSearch}
                  onClick={() => handleCartID(searchCartID)}
                />
                <IonInput
                  className={styles.inputSearch}
                  id="filterCartID"
                  // minlength={4}
                  clearInput
                  placeholder="Buscar por ID carrito"
                  onIonInput={(event: CustomEvent<InputChangeEventDetail>) => {
                    setSearchCartID(event.detail.value ?? '');
                    !event.detail.value && deleteFilterByCartId();
                  }}
                  onKeyUp={(event: any) => {
                    if (event.key === 'Enter') {
                      handleCartID(searchCartID);
                    }
                  }}
                  value={searchCartID}
                />
              </div>
            </div>
          </>
        )}
        {showAGCodeFilter && (
          <>
            <div className={styles.inputSeparator} />
            <div className={styles.searchField}>
              <div className={styles.searchInputContainer}>
                <IonIcon
                  md={searchOutline}
                  className={styles.iconSearch}
                  onClick={() => handleProposalRef(searchProposalRef)}
                />
                <IonInput
                  className={classNames(styles.inputSearch, styles.agCode)}
                  id="filterProposalReference"
                  minlength={MIN_SEARCH_LENGTH}
                  clearInput
                  placeholder="Buscar por código AG"
                  aria-invalid={proposalRefTooShort || undefined}
                  aria-describedby={
                    proposalRefTooShort ? PROPOSAL_REF_LENGTH_ERROR_ID : undefined
                  }
                  onIonInput={(event: CustomEvent<InputChangeEventDetail>) => {
                    setSearchProposalRef(event.detail.value ?? '');
                    setProposalRefTooShort(false);
                    !event.detail.value && deleteFilterByProposalRef();
                  }}
                  onKeyUp={(event: any) => {
                    if (event.key === 'Enter') {
                      handleProposalRef(searchProposalRef);
                    }
                  }}
                  value={searchProposalRef}
                />
              </div>
              {proposalRefTooShort && renderSearchLengthError(PROPOSAL_REF_LENGTH_ERROR_ID)}
            </div>
          </>
        )}
        <div className={styles.calendarFilterField}>
          <div
            className={classNames(
              styles.calendarFilterContainer,
              dateRangeError && styles.invalid,
            )}
            onClick={openDatePicker}
          >
            <div className={styles.calendarFilter}>
              <div className={styles.inputCalendar}>
                <SearchDatePicker
                  ref={datePickerRef}
                  dateStart={setStartDate}
                  dateEnd={setEndDate}
                  savedStartDate={startDate}
                  savedEndDate={endDate}
                  clear={cleared}
                  minDate={getMinAllowedDate().toDate()}
                  maxRangeDays={appliedSpecificFilter ? undefined : MAX_DATE_RANGE_DAYS}
                  errorId={dateRangeErrorIds}
                  availableSpecificFilters={availableSpecificFilters}
                />
              </div>
              <IonIcon src={CalendarIcon} className={styles.iconSearch} />
            </div>
            <div className={styles.selectFilter}>
              <IonText>Selecciona</IonText>
              <IonIcon src={CaretDownRedIcon} className={styles.iconSearch} />
            </div>
          </div>
          {isRangeTooWide && (
            <IonText
              id={DATE_RANGE_INLINE_ERROR_ID}
              className={styles.dateRangeInlineError}
            >
              {getRangeTooWideTitle()}
            </IonText>
          )}
        </div>
      </div>
      <div className={styles.dateRangeMessages} aria-live="polite">
        {filtersErrorMessage && (
          <IonText
            id={DATE_RANGE_ERROR_ID}
            role="alert"
            className={styles.dateRangeError}
          >
            {filtersErrorMessage}
          </IonText>
        )}
        {showSearchScope && (
          <p className={styles.searchScope}>
            <IonIcon icon={informationCircleOutline} aria-hidden="true" />
            <span>
              Búsqueda en los últimos
              {' '}
              <strong>{searchScopePeriod}</strong>
              {showResultsCount && (
                ` · ${dashboardData.length} ${dashboardData.length === 1 ? 'resultado' : 'resultados'}`
              )}
            </span>
          </p>
        )}
      </div>
      <div className={styles.filterStatusDate}>
        <div className={styles.filterType}>
          <IonButton
            className={styles.ionButtonType}
            expand="block"
            onClick={() => setOpen(true)}
          >
            <IonText className={styles.textIconFilter}>
              Filtro por
              <IonIcon md={chevronDownOutline} />
            </IonText>
            {' '}
          </IonButton>
          <IonContent>
            <IonModal isOpen={open} className={styles.modalCalendarPrincipal}>
              <IonToolbar className={styles.toolbar}>
                <IonButtons slot="end">
                  <IonButton
                    onClick={() => setOpen(false)}
                    className={styles.buttonCloseModal}
                  >
                    <IonIcon md={closeOutline} className={styles.iconClose} />
                  </IonButton>
                </IonButtons>
              </IonToolbar>

              <div className={styles.filterCalendar}>
                <FilterCalendar
                  dateStart={setStartDate}
                  dateEnd={setEndDate}
                  proposalReference={(newSearch: string) => {
                    handleProposalRef(newSearch);
                  }}
                  cartID={(newSearch: string) => {
                    handleCartID(newSearch);
                  }}
                  closeModal={() => setOpen(false)}
                  updateFilterValues={updateFilterValues}
                  currentButtons={buttons}
                  updateCurrentButtons={(currentButtons) => setButtons(currentButtons)}
                />
              </div>
            </IonModal>
          </IonContent>
        </div>
        <div className={styles.allButtons}>
          {buttons.map(({ name, initials, isSelected }: Status, idx) => (
            <div
              className={
                isSelected ? styles.buttonStyleSelected : styles.buttonStyle
              }
              key={initials}
              id={initials}
              onClick={() => selectButtons(initials, !isSelected)}
            >
              {idx === 0 && (
                <IonIcon className={styles.matrixIcon} src={DotsMatrixIcon} />
              )}
              <IonText className={isSelected ? styles.selectedText : ''}>
                {name}
              </IonText>
            </div>
          ))}
        </div>
      </div>
      {dashboardData && dashboardData.length > 0 && (
        <DasboardTableDesktopHead
          orderByData={handleOrderByData}
          loadingData={loadingData}
        />
      )}
    </div>
  );

  const triggerNewOperation = () => {
    // si es desktop mas de 991px, se dispara el click del botón de nueva operación, sino dispara el de nueva newOperationButtonApp
    const buttonId = window.innerWidth > 991 ? 'newOperationButton' : 'newOperationButtonApp';
    const button = document.getElementById(buttonId);
    if (button) {
      button.click();
    }
  };
  // const [dataTestBanner, setDataTestBanner] = useState(false);
  const history = useHistory();
  const {
    handlers: statusAlertHandlers,
    activeAlertId: computedAlertId,
    isEventAlert,
    clearOperationOutcome,
    amount,
  } = useAlertHandlersActions({
    triggerNewOperation,
    selectOnlyStatus,
    goToContact: () => history.push(`${ROUTES_PATH.CONTACT}#tel_contact`),
    monthSummaries: storedMonthSummaries,
    operations: unfilteredOperations,
    // Solo evaluamos las reglas métricas cuando ambas APIs ya respondieron.
    // Los avisos por evento no dependen de este flag.
    ready: storedMonthSummaries.length > 0 && !loadingData,
    //  testData: dataTestBanner,
  });

  // Aviso visible = el calculado por las reglas. El "dismiss" métrico solo
  // oculta avisos métricos; los avisos por evento son nuevos y siempre se
  // muestran hasta que el usuario los cierra (vía clearOperationOutcome).
  const visibleAlertId = isEventAlert || !alertDismissed ? computedAlertId : null;

  // Si no hay ofertas comerciales, no podemos abrir el flujo de nueva operación,
  // así que ocultamos el CTA quitando el handler de la alerta que lo muestra.
  const effectiveAlertHandlers = commercialOffers
    ? statusAlertHandlers
    : {
      ...statusAlertHandlers,
      [StatusAlertActionId.NEW_OPERATION]: undefined,
    };

  const resolvedAlert = useResolvedStatusAlert({
    id: visibleAlertId ?? StatusAlertId.PDV_INACTIVE_1M,
    handlers: effectiveAlertHandlers,
    amountalert: amount,
    onClose: () => {
      registerCloseNotificationBanner();
      if (isEventAlert) {
        // Descarta el resultado de operación persistido.
        clearOperationOutcome();
      } else {
        setAlertDismissed(true);
        sessionStorage.setItem('Pepper-AlertDismissed', 'true');
      }
    },
  });

  return (
    <IonPage className={styles.operationDashboard}>
      {showNotifModal && (
        <Notifications
          showModal={showNotifModal}
          closeModal={() => setShowNotifModal(false)}
        />
      )}
      {/* <Banner type={BannerType.OPERATIONS}></Banner> */}
      <IonContent ref={contentRef} scrollEvents className={styles.contentTable}>
        <Banner type={BannerType.OPERATIONS} />
        <div className={styles.dashboardTableWrapper}>
          <div className={styles.dashboardPage}>
            <div className={styles.operationsInfo}>
              <div className={styles.operationsInfoTitles}>
                <IonLabel className={styles.operationsInfoTitle}>
                  Operaciones
                </IonLabel>
                <IonLabel className={styles.operationsInfoSlash}> | </IonLabel>
                <IonLabel className={styles.operationsInfoDetail}>
                  {merchantData?.commercial_name}
                </IonLabel>
                <div
                  className={classNames(styles.filterStatusDate, styles.mobile)}
                >
                  <div className={styles.filterType}>
                    <IonButton
                      className={styles.ionButtonType}
                      expand="block"
                      onClick={() => presentOperationsFilterModal<OperationsFiltersModalProps>(
                        {
                          backdropDismiss: false,
                          onDismiss: () => {},
                          dateStart: setStartDate,
                          dateEnd: setEndDate,
                          proposalReference: (newSearch: string) => {
                            handleProposalRef(newSearch);
                          },
                          cartID: (newSearch: string) => {
                            handleCartID(newSearch);
                          },
                          updateFilterValues,
                          currentButtons: buttons,
                          updateCurrentButtons: (currentButtons) => setButtons(currentButtons),
                          specificFilter: appliedSpecificFilter,
                          availableSpecificFilters,
                          centered: isTabletMini && !isDesktop,
                        },
                      )}
                    >
                      <IonText className={styles.textIconFilter}>
                        Filtrar por
                        <IonIcon md={chevronDownOutline} />
                      </IonText>
                      {' '}
                    </IonButton>
                  </div>
                </div>
              </div>
              <div className={styles.operationsInfoActions}>
                <div className={styles.operationsInfoActionsImport}>
                  <IonLabel className={styles.operationsInfoActionsImportTitle}>
                    Abonado este mes
                  </IonLabel>
                  <IonText className={styles.operationsInfoActionsImportAmount}>
                    {currentMonthSummary
                      && pricesHelper(currentMonthSummary.total_amount, true)}
                  </IonText>
                </div>

                <div className={styles.operationsInfoActionsButtons}>
                  {commercialOffers && (
                    <IonButtons
                      className={styles.operationsInfoActionsIonbutton}
                    >
                      <IonButton
                        className={
                          styles.operationsInfoActionsButtonsNewoperation
                        }
                        expand="block"
                        id="newOperationButton"
                        onClick={() => {
                          if (ecoCommercialOffer) {
                            handleNewOperationManaged();
                          } else if (
                            appVersion
                            && (superExpressFlow !== null
                              ? superExpressFlow
                              : versionControl?.super_express_default)
                            && isNFCSupported
                          ) {
                            handleNewDniEOperation();
                          } else {
                            handleNewOperation();
                          }
                        }}
                      >
                        <IonText>Nueva Operación</IonText>
                        <IonIcon
                          src={PlusIcon}
                          className={styles.operationsInfoActionsIcon}
                        />
                      </IonButton>
                    </IonButtons>
                  )}
                </div>
              </div>
            </div>
            <hr className={styles.separator} />
            <div
              className={classNames(
                styles.banner,
                !showBanner ? styles.hidden : '',
                styles.bannerTransition,
              )}
            >
              <IonLabel className={styles.bannerTitle}>
                {merchantData?.commercial_name}
              </IonLabel>
              <div className={styles.amount}>
                <IonLabel className={styles.amountTitle}>
                  Abonado este mes
                </IonLabel>
                <IonText className={styles.amountPrice}>
                  {currentMonthSummary
                    && pricesHelper(currentMonthSummary.total_amount, true)}
                </IonText>
              </div>
            </div>
            {/* añadir avisos de estado */}
            {/* El métrico se oculta en el flujo eco; el aviso por evento
            se muestra siempre pero se lanza en pepper one solo. */}
            {visibleAlertId && (isEventAlert || !ecoCommercialOffer) && (
              <div className={styles.statusAlertWrapper}>
                <StatusAlert
                  image={resolvedAlert.image}
                  title={resolvedAlert.title}
                  message={resolvedAlert.message}
                  actionLabel={resolvedAlert.actionLabel}
                  onAction={resolvedAlert.onAction}
                  variant={resolvedAlert.variant}
                  onClose={resolvedAlert.onClose}
                />
              </div>
            )}
            {renderFilters(true)}
          </div>
          {/*           {ENVIRONMENT.ENV === 'development' && !ecoCommercialOffer && (
            <IonButton
              onClick={() => {
                setDataTestBanner(true);
              }}
            >
              Test Status Alert
            </IonButton>
          )} */}

          <DashboardTable
            data={dashboardData!}
            loadingData={loadingData}
            isRefreshing={isRefreshing}
            onScrollEnd={async (event?: CustomEvent) => {
              try {
                await searchNext(); // Intenta cargar más datos
              } catch (error) {
                console.error('Error al cargar más datos:', error);
              } finally {
                if (event) {
                  event.detail.complete(); // Detiene el spinner incluso si ocurre un error
                }
              }
            }}
            moreItems={moreItems}
            emptyMessage={isRangeTooWide && dateRangeError
              ? (
                <DateRangeErrorMessage
                  error={dateRangeError}
                  availableFilters={availableSpecificFilters}
                />
              )
              : undefined}
            emptyMessageId={DATE_RANGE_ERROR_ID}
            onChangeShowAGCode={(showAgCode: boolean) => {
              setShowAGCodeFilter(showAgCode);
            }}
          />
        </div>
      </IonContent>
      <div className={styles.buttonException}>
        {commercialOffers && (
          <IonButtons className={styles.operationsInfoActionsIonbutton}>
            <IonButton
              id="newOperationButtonApp"
              className={styles.operationsInfoActionsButtonsNewoperation}
              expand="block"
              onClick={() => {
                if (ecoCommercialOffer) {
                  handleNewOperationManaged();
                } else if (
                  appVersion
                  && (superExpressFlow !== null
                    ? superExpressFlow
                    : versionControl?.super_express_default)
                  && isNFCSupported
                ) {
                  handleNewDniEOperation();
                } else {
                  handleNewOperation();
                }
              }}
            >
              <IonIcon
                md={addCircleOutline}
                className={styles.operationsInfoActionsIcon}
              />
              <IonText className={styles.textNew}>Nueva</IonText>
            </IonButton>
          </IonButtons>
        )}
      </div>
      <IonModal
        isOpen={modalOpen}
        backdropDismiss={false}
        id="pepper-one-modal"
        onDidDismiss={() => setModalOpen(false)}
        className={styles.pepperOneModal}
      >
        <iframe
          src={cexUrl}
          className={styles.pepperIframe}
          title="pepperone"
          allow="microphone; camera; midi; encrypted-media;"
          width="100%"
          height="100%"
          id="pepper-one-iframe"
          onLoad={sendTermsData}
        />
      </IonModal>
    </IonPage>
  );
}
